import { AlertTriangle, CalendarDays, Clock3, FolderKanban } from "lucide-react";

function joinClasses(...values) {
  return values.filter(Boolean).join(" ");
}

const TONE_META = {
  ok: {
    label: "Ruimte",
    card: "border-emerald-100 bg-emerald-50/40",
    pill: "border-emerald-200 bg-emerald-50 text-emerald-700",
    bar: "bg-emerald-500",
    text: "text-emerald-700",
  },
  warning: {
    label: "Bijna vol",
    card: "border-amber-100 bg-amber-50/50",
    pill: "border-amber-200 bg-amber-50 text-amber-700",
    bar: "bg-amber-500",
    text: "text-amber-700",
  },
  over: {
    label: "Overbezet",
    card: "border-rose-100 bg-rose-50/60",
    pill: "border-rose-200 bg-rose-50 text-rose-700",
    bar: "bg-rose-500",
    text: "text-rose-700",
  },
};

export default function PlanningCapacityPanel({
  rows,
  selectedDate,
  onDateChange,
  onOpen,
}) {
  const activeRows = Array.isArray(rows) ? rows : [];
  const totalPlannedHours = activeRows.reduce(
    (sum, row) => sum + Number(row?.plannedHours || 0),
    0
  );
  const totalCapacityHours = activeRows.reduce(
    (sum, row) => sum + Number(row?.maxHours || 0),
    0
  );
  const totalAlerts = activeRows.filter((row) => row.capacityTone !== "ok").length;

  return (
    <section className="rounded-[26px] border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Inzet & Capaciteit
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-950">
              Weekbelasting op monteurs en ploegen
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Deze weergave gebruikt de weekscope van de bestaande planning-overview
              om belasting, open ruimte en overbezetting sneller zichtbaar te maken.
            </p>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Weekfocus
            </span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => onDateChange?.(event.target.value)}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
            />
          </label>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MetricCard
            icon={CalendarDays}
            label="Ingepland deze week"
            value={`${totalPlannedHours.toFixed(1)} uur`}
          />
          <MetricCard
            icon={Clock3}
            label="Beschikbare weekcapaciteit"
            value={`${totalCapacityHours.toFixed(1)} uur`}
          />
          <MetricCard
            icon={AlertTriangle}
            label="Capaciteitswaarschuwingen"
            value={`${totalAlerts}`}
          />
        </div>
      </div>

      <div className="space-y-4 p-4">
        {activeRows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            Geen inzetdata gevonden voor deze weekfocus.
          </div>
        ) : (
          activeRows.map((row) => {
            const meta = TONE_META[row.capacityTone] || TONE_META.ok;
            const progress = Math.min(Number(row.occupancyRatio || 0), 1.25) * 100;
            return (
              <article
                key={row.id}
                className={joinClasses("rounded-2xl border p-4", meta.card)}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-950">
                        {row.name}
                      </h3>
                      <span
                        className={joinClasses(
                          "rounded-full border px-2.5 py-1 text-xs font-semibold",
                          meta.pill
                        )}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {row.itemCount} gepland item(s) • {row.plannedHours.toFixed(1)} /{" "}
                      {row.maxHours.toFixed(1)} uur
                    </p>
                  </div>

                  <div className="min-w-[240px]">
                    <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={joinClasses("h-full rounded-full", meta.bar)}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3 text-[11px]">
                      <span className="text-slate-500">
                        Nog {row.remainingHours.toFixed(1)} uur ruimte
                      </span>
                      <span className={joinClasses("font-semibold", meta.text)}>
                        {Math.round(Number(row.occupancyRatio || 0) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {row.items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
                      Geen werk gekoppeld in de gekozen week.
                    </div>
                  ) : (
                    row.items.map((item) => (
                      <button
                        key={`${row.id}-${item.id}`}
                        type="button"
                        onClick={() => onOpen?.(item)}
                        className="flex w-full items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 text-xs text-slate-500">
                            <CalendarDays className="h-3.5 w-3.5" />
                            <span>{item.plannedDate || "-"}</span>
                            <span>•</span>
                            <span>{item.startTime || "--:--"}</span>
                            <span>•</span>
                            <span>{item.durationLabel || "-"}</span>
                          </span>
                          <span className="mt-1 block truncate text-sm font-semibold text-slate-900">
                            {item.werkbonNummer || "Werkbon"} • {item.title}
                          </span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                            <FolderKanban className="h-3.5 w-3.5" />
                            <span className="truncate">
                              {item.projectLabel || item.clientName || "Zonder project"}
                            </span>
                          </span>
                        </span>

                        {item.hasConflict ? (
                          <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                            Conflict
                          </span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

function MetricCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <p className="mt-2 text-xl font-semibold text-slate-950">{value}</p>
    </div>
  );
}
